import { useState, useRef, useEffect, useCallback } from 'react';
import { docTemplatesAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Printer, Send, Download, X, Type, Plus, Trash2, Eye, FileText, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * DocGenerateDialog - Generates a document from a template + deal data
 * with pre-print text overlay editing support.
 */
export default function DocGenerateDialog({ open, onClose, dealId, clientEmail, clientName, onSent }) {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [customTexts, setCustomTexts] = useState([]);
  const [addingText, setAddingText] = useState(false);
  const [step, setStep] = useState('select'); // select | preview | edit

  useEffect(() => {
    if (open) {
      setStep('select');
      setPdfUrl(null);
      setCustomTexts([]);
      setSelectedTemplateId('');
      docTemplatesAPI.list().then(res => {
        const withPdf = (res.data || []).filter(t => t.page_count > 0);
        setTemplates(withPdf);
      }).catch(() => toast.error('Failed to load templates'));
    }
  }, [open]);

  const handleGenerate = async () => {
    if (!selectedTemplateId) { toast.error('Select a template'); return; }
    setGenerating(true);
    try {
      const res = await docTemplatesAPI.generate(selectedTemplateId, {
        deal_id: dealId,
        custom_texts: customTexts,
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      setStep('preview');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate document');
    }
    setGenerating(false);
  };

  const handleRegenerate = async () => {
    setGenerating(true);
    try {
      const res = await docTemplatesAPI.generate(selectedTemplateId, {
        deal_id: dealId,
        custom_texts: customTexts,
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      setStep('preview');
      toast.success('Document regenerated with edits');
    } catch (err) {
      toast.error('Failed to regenerate');
    }
    setGenerating(false);
  };

  const handlePrint = () => {
    if (pdfUrl) {
      const printWindow = window.open(pdfUrl, '_blank');
      if (printWindow) printWindow.addEventListener('load', () => printWindow.print());
    }
  };

  const handleDownload = () => {
    if (pdfUrl) {
      const a = document.createElement('a');
      a.href = pdfUrl;
      const tmpl = templates.find(t => t.id === selectedTemplateId);
      a.download = `${(tmpl?.name || 'Document').replace(/\s+/g, '_')}.pdf`;
      a.click();
    }
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const res = await docTemplatesAPI.generateAndSend(selectedTemplateId, {
        deal_id: dealId,
        custom_texts: customTexts,
        recipient_email: clientEmail,
      });
      toast.success(res.data.message || 'Document sent');
      onSent?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    }
    setSending(false);
  };

  const addCustomText = () => {
    setCustomTexts(prev => [...prev, { page: 1, x: 100, y: 100, text: '', font_size: 10 }]);
  };

  const updateCustomText = (index, key, val) => {
    setCustomTexts(prev => prev.map((ct, i) => i === index ? { ...ct, [key]: val } : ct));
  };

  const removeCustomText = (index) => {
    setCustomTexts(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="doc-generate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <FileText className="w-4 h-4 text-[#E31937]" />
            {step === 'select' ? 'Generate Document' : step === 'edit' ? 'Edit Before Sending' : 'Document Preview'}
          </DialogTitle>
        </DialogHeader>

        {step === 'select' && (
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Select Template</label>
              <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
                <SelectTrigger className="bg-gray-50" data-testid="select-template">
                  <SelectValue placeholder="Choose a document template..." />
                </SelectTrigger>
                <SelectContent>
                  {templates.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name} ({t.page_count} pages, {(t.fields || []).filter(f => f.tag_key).length} data tags)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {templates.length === 0 && (
                <p className="text-xs text-gray-400 mt-2">No templates with PDFs found. Create one in Doc Templates first.</p>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button className="bg-[#E31937] hover:bg-[#C4122C] text-white" onClick={handleGenerate} disabled={!selectedTemplateId || generating} data-testid="generate-btn">
                {generating ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Generating...</> : <><Eye className="w-4 h-4 mr-1" /> Generate Preview</>}
              </Button>
            </div>
          </div>
        )}

        {step === 'preview' && pdfUrl && (
          <div className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-auto bg-gray-100 rounded">
              <iframe src={pdfUrl} className="w-full h-[60vh]" title="Document Preview" data-testid="pdf-preview" />
            </div>
            <div className="flex items-center justify-between pt-3 border-t mt-3">
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setStep('select')} data-testid="back-to-select">
                  Back
                </Button>
                <Button variant="outline" size="sm" onClick={() => setStep('edit')} data-testid="edit-before-send">
                  <Type className="w-3 h-3 mr-1" /> Edit Before Sending
                </Button>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleDownload} data-testid="download-doc-btn">
                  <Download className="w-3 h-3 mr-1" /> Download
                </Button>
                <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-doc-btn">
                  <Printer className="w-3 h-3 mr-1" /> Print
                </Button>
                <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={handleSend} disabled={sending} data-testid="send-doc-btn">
                  {sending ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Sending...</> : <><Send className="w-3 h-3 mr-1" /> Send to {clientName || 'Client'}</>}
                </Button>
              </div>
            </div>
          </div>
        )}

        {step === 'edit' && (
          <div className="flex flex-col flex-1 overflow-hidden">
            <p className="text-xs text-gray-500 mb-2">Add custom text overlays that will appear on the generated document. After editing, click "Apply & Preview" to see the result.</p>
            <div className="flex-1 overflow-auto space-y-2 max-h-[40vh]">
              {customTexts.map((ct, i) => (
                <div key={i} className="flex items-center gap-2 bg-gray-50 rounded p-2 border border-gray-200" data-testid={`custom-text-${i}`}>
                  <div className="flex-1 space-y-1">
                    <Input placeholder="Text to add..." value={ct.text} onChange={e => updateCustomText(i, 'text', e.target.value)} className="h-7 text-xs" data-testid={`custom-text-input-${i}`} />
                    <div className="flex gap-2">
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] text-gray-400">Page</span>
                        <Input type="number" value={ct.page} onChange={e => updateCustomText(i, 'page', parseInt(e.target.value) || 1)} className="h-6 w-14 text-xs" min={1} />
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] text-gray-400">X</span>
                        <Input type="number" value={ct.x} onChange={e => updateCustomText(i, 'x', parseInt(e.target.value) || 0)} className="h-6 w-16 text-xs" />
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] text-gray-400">Y</span>
                        <Input type="number" value={ct.y} onChange={e => updateCustomText(i, 'y', parseInt(e.target.value) || 0)} className="h-6 w-16 text-xs" />
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] text-gray-400">Size</span>
                        <Input type="number" value={ct.font_size} onChange={e => updateCustomText(i, 'font_size', parseInt(e.target.value) || 10)} className="h-6 w-14 text-xs" min={5} max={24} />
                      </div>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600" onClick={() => removeCustomText(i)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
              {customTexts.length === 0 && (
                <div className="text-center py-6 text-gray-400 text-sm">No custom text added yet</div>
              )}
            </div>
            <div className="flex items-center justify-between pt-3 border-t mt-3">
              <Button variant="outline" size="sm" onClick={addCustomText} data-testid="add-custom-text">
                <Plus className="w-3 h-3 mr-1" /> Add Text
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setStep('preview')}>Cancel</Button>
                <Button size="sm" className="bg-[#E31937] hover:bg-[#C4122C] text-white" onClick={handleRegenerate} disabled={generating} data-testid="apply-edits-btn">
                  {generating ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Applying...</> : 'Apply & Preview'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
